import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Loader2 } from "lucide-react";
import { PushNotificationToggle } from "@/components/PushNotificationToggle";
import { NotificationPreferences } from "@/components/NotificationPreferences";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface ProfileData {
  first_name: string;
  last_name: string;
  company: string;
  contact_phone: string;
  contact_phone_ext: string;
  contact_email: string;
  other_phone: string;
  other_email: string;
}

interface LandlordProfileData {
  billing_address: string;
  billing_city: string;
  billing_state: string;
  billing_zip: string;
}

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [isLandlord, setIsLandlord] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    first_name: "",
    last_name: "",
    company: "",
    contact_phone: "",
    contact_phone_ext: "",
    contact_email: "",
    other_phone: "",
    other_email: "",
  });
  const [landlordProfileData, setLandlordProfileData] = useState<LandlordProfileData>({
    billing_address: "",
    billing_city: "",
    billing_state: "",
    billing_zip: "",
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
        if (session?.user) {
          setEmail(session.user.email || "");
        } else {
          navigate("/auth");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (session?.user) {
        setEmail(session.user.email || "");
      } else {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Fetch profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user) return;

      // Fetch user role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .maybeSingle();

      const userIsLandlord = roleData?.role === "landlord";
      setIsLandlord(userIsLandlord);

      // Fetch profile data
      const { data, error } = await supabase
        .from("profiles")
        .select("first_name, last_name, company, contact_phone, contact_phone_ext, contact_email, other_phone, other_email")
        .eq("id", user.id)
        .maybeSingle();

      if (data) {
        setProfileData({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          company: data.company || "",
          contact_phone: data.contact_phone || "",
          contact_phone_ext: data.contact_phone_ext || "",
          contact_email: data.contact_email || "",
          other_phone: data.other_phone || "",
          other_email: data.other_email || "",
        });
      }

      // Fetch landlord profile data if user is a landlord
      if (userIsLandlord) {
        const { data: landlordData } = await supabase
          .from("landlord_profiles")
          .select("billing_address, billing_city, billing_state, billing_zip")
          .eq("user_id", user.id)
          .maybeSingle();

        if (landlordData) {
          setLandlordProfileData({
            billing_address: landlordData.billing_address || "",
            billing_city: landlordData.billing_city || "",
            billing_state: landlordData.billing_state || "",
            billing_zip: landlordData.billing_zip || "",
          });
        }
      }
    };

    fetchProfileData();
  }, [user]);

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        email: email,
      });

      if (error) throw error;

      toast.success("Check your email to confirm the change");
    } catch (error: any) {
      toast.error(error.message || "Failed to update email");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      // Update profiles table
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          company: profileData.company,
          contact_phone: profileData.contact_phone,
          contact_phone_ext: profileData.contact_phone_ext,
          contact_email: profileData.contact_email,
          other_phone: profileData.other_phone,
          other_email: profileData.other_email,
        })
        .eq("id", user.id);

      if (profileError) throw profileError;

      // Update landlord profile if user is a landlord
      if (isLandlord) {
        const { error: landlordError } = await supabase
          .from("landlord_profiles")
          .update({
            billing_address: landlordProfileData.billing_address,
            billing_city: landlordProfileData.billing_city,
            billing_state: landlordProfileData.billing_state,
            billing_zip: landlordProfileData.billing_zip,
          })
          .eq("user_id", user.id);

        if (landlordError) throw landlordError;
      }

      toast.success("Profile updated successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user?.email || "", {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) throw error;

      toast.success("Password reset email sent");
    } catch (error: any) {
      toast.error(error.message || "Failed to send password reset email");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl font-bold mb-2">Profile Settings</h1>
          <p className="text-muted-foreground mb-8">Manage your account information</p>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        value={profileData.first_name}
                        onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        value={profileData.last_name}
                        onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="company">Company *</Label>
                    <Input
                      id="company"
                      value={profileData.company}
                      onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="contact_phone">Contact Phone *</Label>
                      <Input
                        id="contact_phone"
                        type="tel"
                        value={profileData.contact_phone}
                        onChange={(e) => setProfileData({ ...profileData, contact_phone: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_phone_ext">Ext</Label>
                      <Input
                        id="contact_phone_ext"
                        type="text"
                        value={profileData.contact_phone_ext}
                        onChange={(e) => setProfileData({ ...profileData, contact_phone_ext: e.target.value })}
                        className="mt-1"
                        placeholder="123"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="other_phone">Other Phone</Label>
                    <Input
                      id="other_phone"
                      type="tel"
                      value={profileData.other_phone}
                      onChange={(e) => setProfileData({ ...profileData, other_phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact_email">Contact Email *</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={profileData.contact_email}
                        onChange={(e) => setProfileData({ ...profileData, contact_email: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="other_email">Other Email</Label>
                      <Input
                        id="other_email"
                        type="email"
                        value={profileData.other_email}
                        onChange={(e) => setProfileData({ ...profileData, other_email: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  {isLandlord && (
                    <>
                      <div className="pt-4 border-t">
                        <h3 className="text-lg font-semibold mb-4">Billing Address</h3>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="billing_address">Address</Label>
                            <Input
                              id="billing_address"
                              value={landlordProfileData.billing_address}
                              onChange={(e) => setLandlordProfileData({ ...landlordProfileData, billing_address: e.target.value })}
                              className="mt-1"
                              placeholder="123 Main St"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label htmlFor="billing_city">City</Label>
                              <Input
                                id="billing_city"
                                value={landlordProfileData.billing_city}
                                onChange={(e) => setLandlordProfileData({ ...landlordProfileData, billing_city: e.target.value })}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="billing_state">State</Label>
                              <Input
                                id="billing_state"
                                value={landlordProfileData.billing_state}
                                onChange={(e) => setLandlordProfileData({ ...landlordProfileData, billing_state: e.target.value })}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="billing_zip">Zip</Label>
                              <Input
                                id="billing_zip"
                                value={landlordProfileData.billing_zip}
                                onChange={(e) => setLandlordProfileData({ ...landlordProfileData, billing_zip: e.target.value })}
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <Button type="submit" disabled={loading}>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Update Profile
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
                <CardDescription>View and update your account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>User ID</Label>
                  <Input value={user?.id || ""} disabled className="mt-1" />
                </div>
                <form onSubmit={handleUpdateEmail} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>
                  <Button type="submit" disabled={loading}>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Update Email
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Password</CardTitle>
                <CardDescription>Change your password</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleChangePassword} variant="outline" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Password Reset Email
                </Button>
              </CardContent>
            </Card>

            <NotificationPreferences />

            <Card>
              <CardHeader>
                <CardTitle>Account Created</CardTitle>
                <CardDescription>Your account creation date</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "Unknown"}
                </p>
              </CardContent>
            </Card>

            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="text-destructive">Delete Account</CardTitle>
                <CardDescription>
                  Permanently delete your account and all associated data. This action cannot be undone.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={loading}>
                      Delete My Account
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete your account, profile, and all associated data. 
                        You will be signed out immediately. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={async () => {
                          setLoading(true);
                          try {
                            const { data, error } = await supabase.functions.invoke("delete-user", {
                              body: { targetUserId: user?.id },
                            });
                            if (error) throw error;
                            if (data?.error) throw new Error(data.error);
                            await supabase.auth.signOut();
                            toast.success("Account deleted successfully");
                            navigate("/");
                          } catch (err: any) {
                            toast.error(err.message || "Failed to delete account");
                            setLoading(false);
                          }
                        }}
                      >
                        Delete Account
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Profile;
